import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SimpleFeedback — Minimal floating feedback widget
 * Two questions:
 * 1. "Did this feel clear?" (yes / somewhat / no)
 * 2. "Was anything confusing?" (optional text)
 */
export default function SimpleFeedback({ page }) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [clarity, setClarity] = useState(null);
  const [confusion, setConfusion] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user || submitted) return null;

  const handleSubmit = async () => {
    if (!clarity) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/beta-ops/feedback`, {
        clarity,
        confusion,
        page: page || window.location.pathname,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      toast.success('Thanks for the feedback');
    } catch {
      toast.error('Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 left-4 z-[3000] flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium shadow-lg transition-transform active:scale-95"
        style={{ backgroundColor: 'var(--fy-surface-card)', color: 'var(--fy-text-secondary)', border: '1px solid var(--fy-border-subtle)' }}
        data-testid="feedback-trigger"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Feedback
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 left-4 z-[3000] w-72 rounded-2xl shadow-2xl overflow-hidden"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="feedback-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <span className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Quick Feedback</span>
        <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:opacity-70" data-testid="feedback-close">
          <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Question 1 */}
        <div>
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>Did this feel clear?</p>
          <div className="flex gap-2">
            {[
              { id: 'yes', label: 'Yes' },
              { id: 'somewhat', label: 'Somewhat' },
              { id: 'no', label: 'No' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setClarity(opt.id)}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{
                  backgroundColor: clarity === opt.id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                  color: clarity === opt.id ? '#fff' : 'var(--fy-text-secondary)',
                  border: `1.5px solid ${clarity === opt.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                }}
                data-testid={`clarity-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Question 2 */}
        <div>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Was anything confusing?</p>
          <textarea
            value={confusion}
            onChange={e => setConfusion(e.target.value)}
            placeholder="Optional..."
            maxLength={500}
            rows={2}
            className="w-full px-3 py-2 rounded-lg text-xs resize-none outline-none"
            style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
            data-testid="confusion-input"
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!clarity || submitting}
          className="w-full py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 transition-opacity"
          style={{
            backgroundColor: 'var(--lk-sage)',
            color: '#fff',
            opacity: !clarity || submitting ? 0.5 : 1,
          }}
          data-testid="feedback-submit"
        >
          <Check className="w-3.5 h-3.5" />
          {submitting ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
}
